import {callAction} from 'client/framework';
import type {NavItemZone} from './types';
import {hasLicenseAccess} from 'lib/UserAccessUtil';
import sessionLifecycleActions from 'client/Session/SessionLifecycleActions';
import sessionStore from 'client/Session/SessionStore';

interface NavItems {
  albert: NavItemZone;
  dennis: NavItemZone;
}

const NAV_ITEMS: NavItems = {
  albert: {
    mainNavItems: [
      {
        key: 'library',
        name: 'Library',
        route: '/subjects',
        matchingRoutes: ['/subjects'],
        permissionPredicates: [
          ({isTeacherWithClassSubjects}) =>
            sessionStore.isStudent() ||
            (sessionStore.isAuthor() && !sessionStore.hasTeacherAccess()) ||
            (sessionStore.isTeacher() && !isTeacherWithClassSubjects)
        ]
      },
      {
        key: 'library',
        name: 'Library',
        route: '/subjects/class-subjects',
        matchingRoutes: ['/subjects'],
        permissionPredicates: [
          ({isTeacherWithClassSubjects}) => sessionStore.isTeacher() && isTeacherWithClassSubjects
        ]
      },
      {
        key: 'classes',
        name: 'Classes',
        route: '/classes'
      },
      {
        key: 'reports',
        name: 'Reports',
        route: '/reports',
        permissionPredicates: [() => sessionStore.hasTeacherAccess()]
      },
      {
        key: 'assignments',
        name: 'Assignments',
        route: '/assignments',
        permissionPredicates: [
          () => sessionStore.hasTeacherAccess() && sessionStore.hasValidSession()
        ],
        routesArr: ['/saved', '/assignments', '/assignment-builder/create-assignment']
      },
      {
        key: 'admin',
        name: 'Admin',
        route: '/school-admin',
        permissionPredicates: [
          () => sessionStore.isSchoolAdministrator(),
          () => sessionStore.isSuper()
        ]
      },
      {
        key: 'pricing',
        name: 'Pricing',
        hardLink: true,
        route: '/pricing-schools',
        permissionPredicates: [
          () => {
            const {isReady, value} = hasLicenseAccess();
            return isReady && !value;
          },
          () => sessionStore.isSuper()
        ]
      }
    ],
    dropdownNavItems: [
      {
        icon: 'user',
        key: 'account',
        name: 'My Account',
        route: '/account/profile',
        showSeparator: true
      },
      {
        icon: 'chart-line',
        key: 'data',
        name: 'My Data',
        route: '/my-data',
        showSeparator: true
      },
      {
        icon: 'pen-nib',
        key: 'authoring',
        name: 'Authoring',
        route: '/dennis/content/dashboard',
        permissionPredicates: [
          () => sessionStore.isAuthor() && !sessionStore.hasContentManagerAccess()
        ]
      },
      {
        icon: 'wrench',
        key: 'dennis',
        name: 'Dennis',
        route: '/dennis',
        permissionPredicates: [() => sessionStore.hasContentManagerAccess()]
      },
      {
        icon: 'book',
        hardLink: true,
        key: 'resource-hub',
        name: 'Resource Hub',
        route: '/resource-hub/',
        permissionPredicates: [() => sessionStore.hasValidSession() && !sessionStore.isStudent()]
      },
      {
        icon: 'question-circle',
        key: 'help',
        name: 'Help',
        hardLink: true,
        route: 'https://help.albert.io/en/'
      },
      {
        icon: 'comment-pen',
        key: 'feedback',
        name: 'Rate Albert',
        permissionPredicates: [() => sessionStore.isSuper(), () => sessionStore.isTeacher()]
      },
      {
        icon: 'sign-out',
        key: 'logout',
        name: 'Log Out',
        action: () => callAction(sessionLifecycleActions.END_SESSION)
      }
    ]
  },
  dennis: {
    mainNavItems: [
      {
        key: 'content',
        name: 'Content',
        route: `/dennis/content`,
        permissionPredicates: [() => sessionStore.hasAuthorAccess()]
      },
      {
        key: 'users',
        name: 'Users',
        route: `/dennis/users`,
        permissionPredicates: [() => sessionStore.isSuper()]
      },
      {
        key: 'districts',
        name: 'Districts',
        route: `/dennis/districts`,
        permissionPredicates: [() => sessionStore.isSuper()]
      },
      {
        key: 'schools',
        name: 'Schools',
        route: `/dennis/schools`,
        permissionPredicates: [() => sessionStore.isSuper()]
      }
    ],
    dropdownNavItems: [
      {
        key: 'logout',
        name: 'Log Out',
        action: () => callAction(sessionLifecycleActions.END_SESSION)
      },
      {
        key: 'backToAlbert',
        name: 'Back to Albert',
        route: '/subjects',
        hardLink: true
      }
    ]
  }
};

export default NAV_ITEMS;
